import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, TrendingUp, Users, Clock, Award, X } from 'lucide-react';

const CaseStudiesContainer = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #ffffff;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CaseStudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CaseStudyCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(135,206,235,0.5);
    box-shadow: 0 12px 40px rgba(0,0,0,0.2);
  }
`;

const CardHeader = styled.div`
  padding: 30px;
  border-bottom: 1px solid rgba(135,206,235,0.2);
`;

const CompanyLogo = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const CompanyName = styled.h3`
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 5px;
  font-weight: 600;
`;

const Industry = styled.p`
  color: #87ceeb;
  font-size: 0.9rem;
  margin-bottom: 15px;
`;

const ProjectTitle = styled.h4`
  font-size: 1.1rem;
  color: #ffffff;
  margin-bottom: 10px;
`;

const CardBody = styled.div`
  padding: 30px;
`;

const Description = styled.p`
  color: rgba(255,255,255,0.8);
  line-height: 1.6;
  margin-bottom: 20px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 20px;
`;

const Metric = styled.div`
  text-align: center;
  padding: 15px;
  background: rgba(135,206,235,0.1);
  border-radius: 10px;
  
  .value {
    font-size: 1.5rem;
    font-weight: bold;
    color: #87ceeb;
    margin-bottom: 5px;
  }
  
  .label {
    font-size: 0.8rem;
    color: rgba(255,255,255,0.7);
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const TechTag = styled.span`
  background: rgba(135,206,235,0.2);
  color: #87ceeb;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ViewDetailsButton = styled.button`
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(135,206,235,0.4);
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%);
  border-radius: 20px;
  padding: 40px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid rgba(135,206,235,0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(135,206,235,0.2);
  }
`;

const CaseStudies = () => {
  const [selectedCase, setSelectedCase] = useState(null);

  const caseStudies = [
    {
      id: 1,
      companyName: "TechCorp Solutions",
      industry: "Financial Technology",
      projectTitle: "Digital Banking Platform",
      description: "Developed a comprehensive digital banking platform with AI-powered fraud detection and real-time analytics.",
      fullDescription: "TechCorp Solutions approached us to build a next-generation digital banking platform that could handle millions of transactions while maintaining the highest security standards. Our team developed a microservices-based architecture using React, Node.js, and AWS cloud services. The platform includes AI-powered fraud detection, real-time transaction monitoring, and advanced analytics dashboard. The solution was delivered on time and has been serving over 500,000 active users with 99.9% uptime.",
      metrics: [
        { value: "500K+", label: "Active Users" },
        { value: "99.9%", label: "Uptime" },
        { value: "40%", label: "Cost Reduction" },
        { value: "3x", label: "Performance Boost" }
      ],
      techStack: ["React", "Node.js", "MongoDB", "AWS", "Docker", "Kubernetes"],
      timeline: "8 months",
      teamSize: "12 developers",
      logo: "TC"
    },
    {
      id: 2,
      companyName: "HealthPlus Medical",
      industry: "Healthcare Technology",
      projectTitle: "Telemedicine Platform",
      description: "Built a HIPAA-compliant telemedicine platform connecting patients with healthcare providers globally.",
      fullDescription: "HealthPlus Medical needed a secure, scalable telemedicine platform to connect patients with healthcare providers during the pandemic. We developed a comprehensive solution with video consultations, electronic health records, prescription management, and payment processing. The platform is HIPAA-compliant and supports multiple languages. It has facilitated over 100,000 consultations and helped HealthPlus expand to 15 countries.",
      metrics: [
        { value: "100K+", label: "Consultations" },
        { value: "15", label: "Countries" },
        { value: "95%", label: "Patient Satisfaction" },
        { value: "60%", label: "Cost Savings" }
      ],
      techStack: ["Vue.js", "Python", "PostgreSQL", "WebRTC", "AWS", "Redis"],
      timeline: "10 months",
      teamSize: "15 developers",
      logo: "HP"
    },
    {
      id: 3,
      companyName: "EduTech Global",
      industry: "Education Technology",
      projectTitle: "AI-Powered Learning Platform",
      description: "Created an adaptive learning platform using AI to personalize education for over 1 million students.",
      fullDescription: "EduTech Global wanted to revolutionize online education with AI-powered personalized learning. We built a comprehensive platform that adapts to each student's learning style and pace. The system uses machine learning algorithms to recommend content, track progress, and provide insights to educators. The platform supports multiple content types, real-time collaboration, and advanced analytics. It now serves over 1 million students across 50+ countries.",
      metrics: [
        { value: "1M+", label: "Students" },
        { value: "50+", label: "Countries" },
        { value: "85%", label: "Completion Rate" },
        { value: "4.8/5", label: "User Rating" }
      ],
      techStack: ["Angular", "Java", "MySQL", "TensorFlow", "GCP", "Elasticsearch"],
      timeline: "12 months",
      teamSize: "18 developers",
      logo: "EG"
    },
    {
      id: 4,
      companyName: "RetailMax Enterprise",
      industry: "E-commerce & Retail",
      projectTitle: "Omnichannel Commerce Platform",
      description: "Developed a unified commerce platform integrating online and offline retail operations.",
      fullDescription: "RetailMax Enterprise needed to unify their online and offline retail operations into a single platform. We created an omnichannel commerce solution that synchronizes inventory, customer data, and sales across all channels. The platform includes advanced analytics, AI-powered recommendations, and automated marketing campaigns. It has helped RetailMax increase sales by 150% and improve customer satisfaction significantly.",
      metrics: [
        { value: "150%", label: "Sales Increase" },
        { value: "200+", label: "Store Locations" },
        { value: "5M+", label: "Products" },
        { value: "99.5%", label: "Availability" }
      ],
      techStack: ["React", "Node.js", "MongoDB", "Redis", "AWS", "Stripe"],
      timeline: "14 months",
      teamSize: "20 developers",
      logo: "RM"
    }
  ];

  return (
    <CaseStudiesContainer>
      <SectionTitle>Client Success Stories</SectionTitle>
      <SectionSubtitle>
        Discover how we've helped businesses transform their operations and achieve remarkable results
      </SectionSubtitle>
      
      <CaseStudiesGrid>
        {caseStudies.map((caseStudy, index) => (
          <CaseStudyCard
            key={caseStudy.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            onClick={() => setSelectedCase(caseStudy)}
          >
            <CardHeader>
              <CompanyLogo>{caseStudy.logo}</CompanyLogo>
              <CompanyName>{caseStudy.companyName}</CompanyName>
              <Industry>{caseStudy.industry}</Industry>
              <ProjectTitle>{caseStudy.projectTitle}</ProjectTitle>
            </CardHeader>
            
            <CardBody>
              <Description>{caseStudy.description}</Description>
              
              <MetricsGrid>
                {caseStudy.metrics.slice(0, 2).map((metric, i) => (
                  <Metric key={i}>
                    <div className="value">{metric.value}</div>
                    <div className="label">{metric.label}</div>
                  </Metric>
                ))}
              </MetricsGrid>
              
              <TechStack>
                {caseStudy.techStack.slice(0, 4).map((tech, i) => (
                  <TechTag key={i}>{tech}</TechTag>
                ))}
                {caseStudy.techStack.length > 4 && (
                  <TechTag>+{caseStudy.techStack.length - 4} more</TechTag>
                )}
              </TechStack>
              
              <ViewDetailsButton>
                <ExternalLink size={16} />
                View Full Case Study
              </ViewDetailsButton>
            </CardBody>
          </CaseStudyCard>
        ))}
      </CaseStudiesGrid>

      <AnimatePresence>
        {selectedCase && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCase(null)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={() => setSelectedCase(null)}>
                <X size={24} />
              </CloseButton>
              
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                <CompanyLogo style={{ marginRight: '20px', marginBottom: '0' }}>
                  {selectedCase.logo}
                </CompanyLogo>
                <div>
                  <CompanyName style={{ marginBottom: '5px' }}>{selectedCase.companyName}</CompanyName>
                  <Industry style={{ marginBottom: '0' }}>{selectedCase.industry}</Industry>
                </div>
              </div>
              
              <ProjectTitle style={{ fontSize: '1.5rem', marginBottom: '20px' }}>
                {selectedCase.projectTitle}
              </ProjectTitle>
              
              <Description style={{ fontSize: '1.1rem', marginBottom: '30px' }}>
                {selectedCase.fullDescription}
              </Description>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                {selectedCase.metrics.map((metric, i) => (
                  <Metric key={i}>
                    <div className="value">{metric.value}</div>
                    <div className="label">{metric.label}</div>
                  </Metric>
                ))}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#ffffff', marginBottom: '15px' }}>Technology Stack</h4>
                <TechStack>
                  {selectedCase.techStack.map((tech, i) => (
                    <TechTag key={i}>{tech}</TechTag>
                  ))}
                </TechStack>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <h4 style={{ color: '#ffffff', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Clock size={16} /> Project Timeline
                  </h4>
                  <p style={{ color: 'rgba(255,255,255,0.8)' }}>{selectedCase.timeline}</p>
                </div>
                <div>
                  <h4 style={{ color: '#ffffff', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} /> Team Size
                  </h4>
                  <p style={{ color: 'rgba(255,255,255,0.8)' }}>{selectedCase.teamSize}</p>
                </div>
              </div>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </CaseStudiesContainer>
  );
};

export default CaseStudies;